/*
 * Cart discount code field
 * -------------------------
 * Lets customers apply / remove discount codes directly in the cart drawer and
 * cart page, with live totals. Uses the native Ajax Cart API (`/cart/update.js`
 * with a `discount` field, added in Shopify Editions '25).
 *
 * Important Shopify behavior this works around:
 *   Manual (code) discounts are NOT exposed on the Liquid `cart` object, so we
 *   cannot render applied codes server-side. We read them from `/cart.js` JSON
 *   and render the applied-codes list + savings with JavaScript instead.
 *
 * DOM contract (see snippets/cart-discount-form.liquid):
 *   [data-cart-discount]            wrapper (may appear more than once)
 *     [data-cart-discount-input]    text input
 *     [data-cart-discount-apply]    apply button
 *     [data-cart-discount-message]  status / error line
 *     [data-cart-discount-list]     <ul> the applied codes render into
 */
(function () {
  'use strict';

  var UPDATE_URL = '/cart/update.js';
  var CART_URL = '/cart.js';

  function acquireDiscountMutation(owner) {
    var lock = window.__themeCartDiscountMutationLock;
    if (!lock) {
      lock = { owner: null };
      window.__themeCartDiscountMutationLock = lock;
    }
    if (lock.owner) return false;
    lock.owner = owner;
    return true;
  }

  function releaseDiscountMutation(owner) {
    var lock = window.__themeCartDiscountMutationLock;
    if (lock && lock.owner === owner) lock.owner = null;
  }

  // Matches the theme's own money handling (USD, cents -> $X.XX).
  function formatMoney(cents) {
    return '$' + (Math.round(cents || 0) / 100).toFixed(2);
  }

  function discountIcon() {
    return (
      '<svg class="cart-discount__icon" width="14" height="14" viewBox="0 0 24 24" ' +
      'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true">' +
      '<line x1="19" y1="5" x2="5" y2="19"></line>' +
      '<circle cx="6.5" cy="6.5" r="2.5"></circle>' +
      '<circle cx="17.5" cy="17.5" r="2.5"></circle></svg>'
    );
  }

  function fetchCart() {
    return fetch(CART_URL, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  /*
   * Applied CODE discounts (excludes automatic discounts, which must not be
   * re-sent to update.js). Reads, in order of reliability:
   *   1. cart.discount_codes  (newer Ajax API: [{code, applicable}])
   *   2. cart_level + line-level applications whose type is 'discount_code'
   */
  function getAppliedCodes(cart) {
    var out = [];
    var seen = {};
    function add(code) {
      if (!code) return;
      var k = String(code).toLowerCase();
      if (seen[k]) return;
      seen[k] = true;
      out.push(String(code));
    }

    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (dc) {
        if (dc && dc.code && dc.applicable !== false) add(dc.code);
      });
    }
    if (cart && Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach(function (app) {
        if (app && app.type === 'discount_code' && app.title) add(app.title);
      });
    }
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        (item.line_level_discount_allocations || []).forEach(function (al) {
          var da = al && al.discount_application;
          if (da && da.type === 'discount_code' && da.title) add(da.title);
        });
      });
    }
    return out;
  }

  // Broader than getAppliedCodes: does ANY applied discount (code or automatic)
  // carry this title? Used to confirm an apply succeeded even on API versions
  // that omit the `type` field on discount applications.
  function hasDiscountTitle(cart, code) {
    var target = String(code).toLowerCase();
    var found = false;
    (cart.cart_level_discount_applications || []).forEach(function (app) {
      if (app && app.title && String(app.title).toLowerCase() === target) found = true;
    });
    (cart.items || []).forEach(function (item) {
      (item.discounts || []).forEach(function (d) {
        if (d && d.title && String(d.title).toLowerCase() === target) found = true;
      });
      (item.line_level_discount_allocations || []).forEach(function (al) {
        var da = al && al.discount_application;
        if (da && da.title && String(da.title).toLowerCase() === target) found = true;
      });
    });
    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach(function (dc) {
        if (dc && dc.code && String(dc.code).toLowerCase() === target && dc.applicable !== false) found = true;
      });
    }
    return found;
  }

  // Total amount (in cents) allocated to a given code across cart + line level.
  function amountForCode(cart, code) {
    var total = 0;
    var target = String(code).toLowerCase();
    (cart.cart_level_discount_applications || []).forEach(function (app) {
      if (app && app.title && String(app.title).toLowerCase() === target) {
        total += app.total_allocated_amount || 0;
      }
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (al) {
        var da = al && al.discount_application;
        if (da && da.title && String(da.title).toLowerCase() === target) {
          total += al.amount || 0;
        }
      });
    });
    return total;
  }

  function setMessage(scope, text, type) {
    scope.querySelectorAll('[data-cart-discount-message]').forEach(function (el) {
      if (!text) {
        el.hidden = true;
        el.textContent = '';
        el.classList.remove('is-error', 'is-success');
        return;
      }
      el.hidden = false;
      el.textContent = text;
      el.classList.toggle('is-error', type === 'error');
      el.classList.toggle('is-success', type === 'success');
    });
  }

  // Render the applied-code chips into every discount widget on the page.
  function renderCodes(cart) {
    var codes = getAppliedCodes(cart);
    document.querySelectorAll('[data-cart-discount-list]').forEach(function (list) {
      list.innerHTML = '';
      codes.forEach(function (code) {
        var amount = amountForCode(cart, code);
        var li = document.createElement('li');
        li.className = 'cart-discount__code';
        li.setAttribute('data-code', code);
        li.innerHTML =
          discountIcon() +
          '<span class="cart-discount__code-label">' + code + '</span>' +
          (amount > 0 ? '<span class="cart-discount__amount">-' + formatMoney(amount) + '</span>' : '') +
          '<button type="button" class="cart-discount__remove" data-cart-discount-remove="' +
          code.replace(/"/g, '&quot;') + '" aria-label="Remove discount ' + code + '">&times;</button>';
        list.appendChild(li);
      });
    });
    return codes;
  }

  // Reflect discounted totals in the theme's existing total displays.
  function updateTotals(cart, dispatch) {
    document.querySelectorAll('[data-hulkapps-cart-total]').forEach(function (el) {
      el.dataset.totalPrice = cart.total_price;
      // The cart-page subtotal shows the pre-discount original total (unchanged
      // by codes); only the drawer subtotal shows the live total_price.
      if (!el.hasAttribute('data-cart-page-total')) {
        el.textContent = formatMoney(cart.total_price);
      }
    });

    // Drawer checkout button + estimated total (no shipping/tax in the drawer).
    document.querySelectorAll('.dropdown-checkout-total').forEach(function (el) {
      el.textContent = formatMoney(cart.total_price);
    });
    document.querySelectorAll('.dropdown-estimated-total-value').forEach(function (el) {
      el.innerHTML = '<strong>' + formatMoney(cart.total_price) + '</strong>';
    });

    // Let the theme recompute cart-page estimated total + checkout button
    // (they read data-total-price and add shipping/tax/protection).
    if (dispatch) {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  }

  function refresh() {
    return fetchCart().then(function (cart) {
      renderCodes(cart);
      updateTotals(cart, false);
      return cart;
    });
  }

  function setBusy(scope, busy) {
    scope.querySelectorAll('[data-cart-discount-apply]').forEach(function (btn) {
      btn.disabled = busy;
      btn.classList.toggle('is-loading', busy);
    });
  }

  function postDiscount(codesArray) {
    return fetch(UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codesArray.join(',') })
    }).then(function (r) {
      return r.json().then(function (data) { return { ok: r.ok, data: data }; })
        .catch(function () { return { ok: r.ok, data: null }; });
    });
  }

  function apply(scope, rawCode) {
    var code = (rawCode || '').trim();
    if (!code) {
      setMessage(scope, 'Enter a discount code.', 'error');
      return;
    }
    var mutationOwner = {};
    if (!acquireDiscountMutation(mutationOwner)) {
      setMessage(scope, 'Another discount update is in progress. Please try again.', 'error');
      return;
    }
    setBusy(scope, true);
    setMessage(scope, 'Applying…', null);

    fetchCart()
      .then(function (cart) {
        var existing = getAppliedCodes(cart);
        var already = existing.some(function (c) { return c.toLowerCase() === code.toLowerCase(); });
        var list = already ? existing : existing.concat([code]);
        return postDiscount(list);
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        var codes = renderCodes(cart);
        updateTotals(cart, true);
        var applied = codes.some(function (c) { return c.toLowerCase() === code.toLowerCase(); })
          || hasDiscountTitle(cart, code);
        if (applied) {
          scope.querySelectorAll('[data-cart-discount-input]').forEach(function (i) { i.value = ''; });
          setMessage(scope, 'Discount "' + code + '" applied.', 'success');
        } else {
          setMessage(scope, '"' + code + '" isn’t valid or doesn’t apply to your cart.', 'error');
        }
      })
      .catch(function () {
        setMessage(scope, 'Something went wrong. Please try again.', 'error');
      })
      .then(function () {
        setBusy(scope, false);
        releaseDiscountMutation(mutationOwner);
      }, function () {
        setBusy(scope, false);
        releaseDiscountMutation(mutationOwner);
      });
  }

  function remove(scope, code) {
    var mutationOwner = {};
    if (!acquireDiscountMutation(mutationOwner)) {
      setMessage(scope, 'Another discount update is in progress. Please try again.', 'error');
      return;
    }
    setBusy(scope, true);
    fetchCart()
      .then(function (cart) {
        var remaining = getAppliedCodes(cart).filter(function (c) {
          return c.toLowerCase() !== String(code).toLowerCase();
        });
        return postDiscount(remaining);
      })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        renderCodes(cart);
        updateTotals(cart, true);
        setMessage(scope, '', null);
      })
      .catch(function () {
        setMessage(scope, 'Could not remove the code. Please try again.', 'error');
      })
      .then(function () {
        setBusy(scope, false);
        releaseDiscountMutation(mutationOwner);
      }, function () {
        setBusy(scope, false);
        releaseDiscountMutation(mutationOwner);
      });
  }

  function closestScope(el) {
    return el.closest('[data-cart-discount]');
  }

  // Delegated events survive drawer re-renders.
  document.addEventListener('click', function (e) {
    var applyBtn = e.target.closest('[data-cart-discount-apply]');
    if (applyBtn) {
      e.preventDefault();
      var scope = closestScope(applyBtn);
      if (!scope) return;
      var input = scope.querySelector('[data-cart-discount-input]');
      apply(scope, input ? input.value : '');
      return;
    }
    var removeBtn = e.target.closest('[data-cart-discount-remove]');
    if (removeBtn) {
      e.preventDefault();
      var scope2 = closestScope(removeBtn);
      if (!scope2) return;
      remove(scope2, removeBtn.getAttribute('data-cart-discount-remove'));
    }
  });

  // Enter key inside the input applies the code (input is not in a <form>).
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-cart-discount-input]');
    if (!input) return;
    e.preventDefault();
    var scope = closestScope(input);
    if (scope) apply(scope, input.value);
  });

  // Populate applied codes + totals on load and whenever the cart changes.
  function init() {
    if (!document.querySelector('[data-cart-discount]')) return;
    refresh();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('cart:updated', function () {
    if (document.querySelector('[data-cart-discount]')) refresh();
  });
})();
